import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Toaster } from 'sonner';
import { ErrorNote, Loading } from './components/ui';
import { ApiError } from './lib/api';
import { useAuthMode, useMe } from './lib/hooks';
import { getRegion } from './lib/regions';
import { loadSession } from './lib/session';
import { Login } from './pages/Login';
import { router } from './router';
import './styles.css';

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 10_000, refetchOnWindowFocus: false, retry: (n, err) => !(err instanceof ApiError && err.status < 500) && n < 2 } },
});

/** Gate: dev mode needs no session; otherwise sign in, then require workspace membership. */
const App = () => {
  const mode = useAuthMode();
  const [, force] = useState(0);
  const hasSession = Boolean(loadSession());
  const me = useMe();

  useEffect(() => {
    const onOut = () => force((n) => n + 1);
    window.addEventListener('am:signed-out', onOut);
    return () => window.removeEventListener('am:signed-out', onOut);
  }, []);

  if (mode.isLoading) return <Loading label="Connecting…" />;
  if (mode.error)
    return (
      <div className="mx-auto max-w-lg p-8">
        <ErrorNote error={new Error(`Can't reach the ${getRegion().label} Agent Manager API (${getRegion().apiBase}).`)} />
      </div>
    );
  const needsLogin = mode.data?.mode !== 'dev' && (!hasSession || (me.error instanceof ApiError && me.error.status === 401));
  if (needsLogin) return <Login onSignedIn={() => force((n) => n + 1)} />;
  if (me.isLoading) return <Loading />;
  if (me.error)
    return (
      <div className="mx-auto max-w-lg p-8">
        <ErrorNote error={me.error} />
        <p className="mt-3 text-[13px] text-ink-2">Ask an admin to add you under Settings → Members.</p>
      </div>
    );
  return <RouterProvider router={router} />;
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <App />
      <Toaster position="bottom-right" richColors closeButton />
    </QueryClientProvider>
  </StrictMode>,
);
